/**
 * site-notice.js
 *
 * Behaviour for snippets/site-notice.liquid: a notice fixed over the header
 * that is rendered hidden and revealed by script, because Liquid cannot read
 * the query parameters most notices are triggered by.
 *
 * A notice can be revealed two ways:
 *
 *   1. Declaratively. Give the element data-reveal-param (and optionally
 *      data-reveal-value, default "1") and it shows on load when that query
 *      parameter matches. Add data-link-param to fill its link from another
 *      parameter, which is only honoured when the value is a path on this
 *      store.
 *
 *   2. From script, for anything a parameter match cannot express:
 *
 *        window.CorstonSiteNotice.show('my-notice', {
 *          tokens: {count: 2, region: 'France'}, // fills {count} and {region}
 *          text: {body: '...'},                  // replaces a text slot first
 *          linkHref: '/some/path',               // same-store paths only
 *          clearParams: ['a', 'b']               // removed from the URL after
 *        });
 *
 * Tokens are written with single braces in the locale strings ({count}) so
 * that Shopify's own {{ }} translation interpolation leaves them alone. Text is
 * always set as text, never markup.
 *
 * Only one notice is visible at a time. A notice with a higher data-priority
 * displaces a lower one; a lower one asked to show while a higher one is up
 * stays hidden. Dismissal is by the close control, Escape, or scrolling past
 * the notice; the query parameters that triggered it are taken back out of the
 * URL so a reload or shared link does not show it again.
 */
(function ($) {
  if (!$ || window.CorstonSiteNotice) return;

  // Far enough that neither a restored scroll position nor a stray touch
  // counts as the visitor scrolling past the notice
  var DISMISS_ON_SCROLL_AFTER = 64;
  // Matches the transition in site-notice.css
  var FADE_MS = 300;

  function $notice(id) {
    return $(document.getElementById(id)).filter('.site-notice');
  }

  function priorityOf($el) {
    return parseInt($el.attr('data-priority'), 10) || 0;
  }

  function $visible() {
    return $('.site-notice').not('[hidden]').not('.site-notice--dismissed');
  }

  // A value that came from the URL is only trusted as far as being a path on
  // this store: a leading single slash, and no scheme or host that could turn
  // the link into an off-site one
  function isStorePath(path) {
    return (
      typeof path === 'string' &&
      path.charAt(0) === '/' &&
      path.charAt(1) !== '/' &&
      path.indexOf(':') === -1
    );
  }

  function fill(template, tokens) {
    return String(template).replace(/\{\s*(\w+)\s*\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(tokens, key) ? String(tokens[key]) : match;
    });
  }

  function splitList(value) {
    return String(value || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  function clearParams(names) {
    if (!names || !names.length) return;
    var params = new URLSearchParams(window.location.search);
    var before = params.toString();
    names.forEach(function (name) { params.delete(name); });
    if (params.toString() === before) return;
    var query = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? '?' + query : '') + window.location.hash
    );
  }

  function dismiss(id, immediately) {
    var $el = $notice(id);
    if (!$el.length || $el.hasClass('site-notice--dismissed')) return;

    $el.addClass('site-notice--dismissed');
    $(window).off('scroll.siteNotice-' + id);
    $(document).off('keydown.siteNotice-' + id);

    // Taken out of the layout and the accessibility tree once it has faded,
    // so it can never intercept a click meant for the header
    var hide = function () { $el.prop('hidden', true); };
    if (immediately) {
      hide();
    } else {
      window.setTimeout(hide, FADE_MS);
    }
  }

  function show(id, options) {
    options = options || {};
    var $el = $notice(id);
    if (!$el.length) return false;

    var priority = priorityOf($el);
    var outranked = false;
    $visible().each(function () {
      var $other = $(this);
      if ($other.attr('id') === id) return;
      if (priorityOf($other) >= priority) {
        outranked = true;
      } else {
        dismiss($other.attr('id'), true);
      }
    });
    if (outranked) return false;

    var tokens = options.tokens || {};
    var text = options.text || {};
    $el.find('[data-site-notice-text]').each(function () {
      var $slot = $(this);
      var name = $slot.attr('data-site-notice-text');
      var template = text[name] != null ? text[name] : $slot.attr('data-template');
      if (template == null) template = $slot.text();
      $slot.text(fill(template, tokens));
    });

    var $link = $el.find('.site-notice__link');
    if ($link.length && options.linkHref !== undefined) {
      if (isStorePath(options.linkHref)) {
        $link.attr('href', options.linkHref).prop('hidden', false);
      } else {
        $link.prop('hidden', true);
      }
    }

    $el.removeClass('site-notice--dismissed').prop('hidden', false);

    $el.find('.site-notice__dismiss')
      .off('click.siteNotice')
      .on('click.siteNotice', function () { dismiss(id); });

    $(window).off('scroll.siteNotice-' + id).on('scroll.siteNotice-' + id, function () {
      if ($(window).scrollTop() > DISMISS_ON_SCROLL_AFTER) dismiss(id);
    });

    $(document).off('keydown.siteNotice-' + id).on('keydown.siteNotice-' + id, function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') dismiss(id);
    });

    clearParams(options.clearParams || splitList($el.attr('data-clear-params')));
    return true;
  }

  // Declarative reveals, once the notices are in the document
  $(function () {
    var params = new URLSearchParams(window.location.search);
    $('.site-notice[data-reveal-param]').each(function () {
      var $el = $(this);
      var expected = $el.attr('data-reveal-value') || '1';
      if (params.get($el.attr('data-reveal-param')) !== expected) return;

      var linkParam = $el.attr('data-link-param');
      show($el.attr('id'), {
        linkHref: linkParam ? params.get(linkParam) : undefined
      });
    });
  });

  window.CorstonSiteNotice = {
    show: show,
    dismiss: dismiss,
    clearParams: clearParams
  };
})(window.jQuery);
